import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Input,
  Select,
  InputNumber,
  Table,
  Tag,
  Dropdown,
  Button,
  Collapse,
  Tooltip,
} from "antd";
import type { MenuProps } from "antd";
import type { AppDispatch } from "../../../../core/data/redux/store";
import {
  selectRoundTemplates,
  selectRoundTemplatesLoading,
} from "../../../../core/data/redux/recruitment/selectors";
import {
  fetchRoundTemplates,
  createRoundTemplate,
  updateRoundTemplate,
  deleteRoundTemplate,
} from "../../../../core/data/redux/recruitment/thunks";
import { notify } from "../../../../services/toast.config";

const { TextArea } = Input;
const { Panel } = Collapse;

// ────────────────── Round type options ──────────────────
const ROUND_TYPE_OPTIONS = [
  { value: "screening", label: "Screening" },
  { value: "technical_round", label: "Technical Round" },
  { value: "coding_test", label: "Coding Test" },
  { value: "hr_round", label: "HR Round" },
  { value: "cultural_fit", label: "Cultural Fit" },
  { value: "managerial_round", label: "Managerial Round" },
  { value: "case_study", label: "Case Study" },
  { value: "group_discussion", label: "Group Discussion" },
  { value: "final_round", label: "Final Round" },
  { value: "assignment", label: "Assignment" },
];

// ────────────────── Seed (pre-built) templates ──────────────────
interface SeedStage {
  round_name: string;
  round_type: string;
  duration_minutes: number;
  is_mandatory: boolean;
  criteria_schema: { key: string; label: string; weight: number }[];
}

interface SeedTemplate {
  template_name: string;
  description: string;
  category: string;
  stages: SeedStage[];
}

const SEED_TEMPLATES: SeedTemplate[] = [
  // ── Sales ──
  {
    template_name: "SDR Hiring Process",
    description: "3-round pipeline for Sales Development Representatives",
    category: "Sales",
    stages: [
      {
        round_name: "Phone Screening",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "communication", label: "Communication Skills", weight: 40 },
          { key: "motivation", label: "Motivation & Drive", weight: 30 },
          { key: "product_knowledge", label: "Product Knowledge", weight: 30 },
        ],
      },
      {
        round_name: "Sales Pitch Simulation",
        round_type: "case_study",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "pitch_quality", label: "Pitch Quality", weight: 35 },
          {
            key: "objection_handling",
            label: "Objection Handling",
            weight: 35,
          },
          { key: "closing_skills", label: "Closing Skills", weight: 30 },
        ],
      },
      {
        round_name: "Culture Fit Interview",
        round_type: "cultural_fit",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "team_fit", label: "Team Fit", weight: 40 },
          { key: "values_alignment", label: "Values Alignment", weight: 30 },
          { key: "growth_mindset", label: "Growth Mindset", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "Account Executive Pipeline",
    description: "4-round pipeline for Account Executives",
    category: "Sales",
    stages: [
      {
        round_name: "Phone Screen",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "experience", label: "Relevant Experience", weight: 40 },
          { key: "communication", label: "Communication", weight: 30 },
          { key: "motivation", label: "Motivation", weight: 30 },
        ],
      },
      {
        round_name: "Case Study",
        round_type: "case_study",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "analysis", label: "Problem Analysis", weight: 30 },
          { key: "strategy", label: "Strategy", weight: 40 },
          { key: "presentation", label: "Presentation", weight: 30 },
        ],
      },
      {
        round_name: "Sales Presentation",
        round_type: "technical_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "demo_skills", label: "Demo Skills", weight: 40 },
          { key: "discovery", label: "Discovery Questions", weight: 30 },
          { key: "value_prop", label: "Value Proposition", weight: 30 },
        ],
      },
      {
        round_name: "Final Interview",
        round_type: "final_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "leadership", label: "Leadership Potential", weight: 35 },
          { key: "culture", label: "Culture Fit", weight: 35 },
          { key: "compensation", label: "Compensation Alignment", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "Sales Manager Evaluation",
    description: "4-round pipeline for Sales Management roles",
    category: "Sales",
    stages: [
      {
        round_name: "Phone Screen",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "track_record", label: "Track Record", weight: 50 },
          { key: "leadership_exp", label: "Leadership Experience", weight: 50 },
        ],
      },
      {
        round_name: "Leadership Assessment",
        round_type: "managerial_round",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "team_management", label: "Team Management", weight: 35 },
          { key: "coaching", label: "Coaching Ability", weight: 35 },
          {
            key: "conflict_resolution",
            label: "Conflict Resolution",
            weight: 30,
          },
        ],
      },
      {
        round_name: "Revenue Strategy",
        round_type: "case_study",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "market_analysis", label: "Market Analysis", weight: 30 },
          { key: "forecasting", label: "Forecasting", weight: 35 },
          { key: "go_to_market", label: "Go-to-Market Strategy", weight: 35 },
        ],
      },
      {
        round_name: "Final Panel",
        round_type: "final_round",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "vision", label: "Vision & Strategy", weight: 40 },
          { key: "cultural_fit", label: "Cultural Fit", weight: 30 },
          {
            key: "executive_presence",
            label: "Executive Presence",
            weight: 30,
          },
        ],
      },
    ],
  },
  // ── Digital Marketing ──
  {
    template_name: "Content Writer Assessment",
    description: "3-round pipeline for Content Writers",
    category: "Digital Marketing",
    stages: [
      {
        round_name: "Portfolio Review",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "writing_quality", label: "Writing Quality", weight: 40 },
          { key: "versatility", label: "Content Versatility", weight: 30 },
          { key: "seo_knowledge", label: "SEO Knowledge", weight: 30 },
        ],
      },
      {
        round_name: "Writing Test",
        round_type: "assignment",
        duration_minutes: 90,
        is_mandatory: true,
        criteria_schema: [
          { key: "clarity", label: "Clarity & Structure", weight: 35 },
          { key: "creativity", label: "Creativity", weight: 35 },
          { key: "grammar", label: "Grammar & Tone", weight: 30 },
        ],
      },
      {
        round_name: "Culture Fit",
        round_type: "cultural_fit",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "team_fit", label: "Team Fit", weight: 40 },
          { key: "deadlines", label: "Deadline Management", weight: 30 },
          { key: "feedback", label: "Feedback Reception", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "SEO/SEM Specialist",
    description: "3-round pipeline for SEO/SEM specialists",
    category: "Digital Marketing",
    stages: [
      {
        round_name: "Technical Screening",
        round_type: "technical_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "seo_technical", label: "Technical SEO", weight: 35 },
          { key: "tools", label: "Tools Proficiency", weight: 35 },
          { key: "analytics", label: "Analytics Ability", weight: 30 },
        ],
      },
      {
        round_name: "Case Study",
        round_type: "case_study",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "strategy", label: "SEO Strategy", weight: 40 },
          { key: "competitive", label: "Competitive Analysis", weight: 30 },
          { key: "roi", label: "ROI Focus", weight: 30 },
        ],
      },
      {
        round_name: "Manager Interview",
        round_type: "managerial_round",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "collaboration", label: "Collaboration", weight: 35 },
          { key: "initiative", label: "Initiative", weight: 35 },
          { key: "culture", label: "Culture Fit", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "Social Media Manager",
    description: "3-round pipeline for Social Media Managers",
    category: "Digital Marketing",
    stages: [
      {
        round_name: "Portfolio Presentation",
        round_type: "screening",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "campaign_quality", label: "Campaign Quality", weight: 40 },
          {
            key: "platform_expertise",
            label: "Platform Expertise",
            weight: 30,
          },
          { key: "analytics", label: "Analytics Skills", weight: 30 },
        ],
      },
      {
        round_name: "Campaign Strategy",
        round_type: "case_study",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "ideation", label: "Creative Ideation", weight: 35 },
          { key: "targeting", label: "Audience Targeting", weight: 35 },
          { key: "budget", label: "Budget Management", weight: 30 },
        ],
      },
      {
        round_name: "Final Interview",
        round_type: "final_round",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "brand_sense", label: "Brand Sensibility", weight: 35 },
          { key: "trend_awareness", label: "Trend Awareness", weight: 35 },
          { key: "team_fit", label: "Team Fit", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "Brand Manager Pipeline",
    description: "4-round pipeline for Brand Managers",
    category: "Digital Marketing",
    stages: [
      {
        round_name: "Phone Screen",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "experience", label: "Brand Experience", weight: 50 },
          {
            key: "strategic_thinking",
            label: "Strategic Thinking",
            weight: 50,
          },
        ],
      },
      {
        round_name: "Brand Case Study",
        round_type: "case_study",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "brand_strategy", label: "Brand Strategy", weight: 40 },
          { key: "market_research", label: "Market Research", weight: 30 },
          { key: "creativity", label: "Creative Execution", weight: 30 },
        ],
      },
      {
        round_name: "Stakeholder Presentation",
        round_type: "technical_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "presentation", label: "Presentation Skills", weight: 35 },
          { key: "persuasion", label: "Persuasion Ability", weight: 35 },
          { key: "data_driven", label: "Data-Driven Thinking", weight: 30 },
        ],
      },
      {
        round_name: "Final Panel",
        round_type: "final_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "leadership", label: "Leadership", weight: 35 },
          { key: "vision", label: "Brand Vision", weight: 35 },
          { key: "culture_fit", label: "Culture Fit", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "Performance Marketing",
    description: "3-round pipeline for Performance Marketing roles",
    category: "Digital Marketing",
    stages: [
      {
        round_name: "Technical Assessment",
        round_type: "technical_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "platform_mastery", label: "Platform Mastery", weight: 35 },
          { key: "data_analysis", label: "Data Analysis", weight: 35 },
          { key: "optimization", label: "Campaign Optimization", weight: 30 },
        ],
      },
      {
        round_name: "Campaign Analysis",
        round_type: "case_study",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          {
            key: "performance_metrics",
            label: "Performance Metrics",
            weight: 40,
          },
          { key: "budget_allocation", label: "Budget Allocation", weight: 30 },
          { key: "reporting", label: "Reporting Quality", weight: 30 },
        ],
      },
      {
        round_name: "Manager Interview",
        round_type: "managerial_round",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          {
            key: "strategic_thinking",
            label: "Strategic Thinking",
            weight: 35,
          },
          { key: "collaboration", label: "Collaboration", weight: 35 },
          { key: "culture", label: "Culture Fit", weight: 30 },
        ],
      },
    ],
  },
  // ── IT / Engineering ──
  {
    template_name: "Frontend Developer",
    description: "4-round pipeline for Frontend Developers",
    category: "IT / Engineering",
    stages: [
      {
        round_name: "Phone Screen",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "fundamentals", label: "JS/CSS Fundamentals", weight: 40 },
          { key: "experience", label: "Relevant Experience", weight: 30 },
          { key: "communication", label: "Communication", weight: 30 },
        ],
      },
      {
        round_name: "Coding Assessment",
        round_type: "coding_test",
        duration_minutes: 90,
        is_mandatory: true,
        criteria_schema: [
          { key: "code_quality", label: "Code Quality", weight: 30 },
          { key: "problem_solving", label: "Problem Solving", weight: 40 },
          { key: "best_practices", label: "Best Practices", weight: 30 },
        ],
      },
      {
        round_name: "System Design",
        round_type: "technical_round",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "architecture", label: "Architecture Thinking", weight: 35 },
          { key: "scalability", label: "Scalability Focus", weight: 35 },
          { key: "ux_sense", label: "UX Sensibility", weight: 30 },
        ],
      },
      {
        round_name: "Culture Fit",
        round_type: "cultural_fit",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "team_work", label: "Teamwork", weight: 35 },
          { key: "learning", label: "Learning Attitude", weight: 35 },
          { key: "values", label: "Values Alignment", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "Backend Developer",
    description: "4-round pipeline for Backend Developers",
    category: "IT / Engineering",
    stages: [
      {
        round_name: "Phone Screen",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "fundamentals", label: "Backend Fundamentals", weight: 40 },
          { key: "experience", label: "Experience", weight: 30 },
          { key: "communication", label: "Communication", weight: 30 },
        ],
      },
      {
        round_name: "Coding Test",
        round_type: "coding_test",
        duration_minutes: 90,
        is_mandatory: true,
        criteria_schema: [
          { key: "algorithms", label: "Algorithms & DS", weight: 40 },
          { key: "code_quality", label: "Code Quality", weight: 30 },
          { key: "testing", label: "Testing Approach", weight: 30 },
        ],
      },
      {
        round_name: "System Design",
        round_type: "technical_round",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "architecture", label: "System Architecture", weight: 35 },
          { key: "database", label: "Database Design", weight: 35 },
          { key: "scalability", label: "Scalability", weight: 30 },
        ],
      },
      {
        round_name: "Final Panel",
        round_type: "final_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "team_fit", label: "Team Fit", weight: 35 },
          { key: "ownership", label: "Ownership Mindset", weight: 35 },
          { key: "growth", label: "Growth Potential", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "Full Stack Developer",
    description: "4-round pipeline for Full Stack Developers",
    category: "IT / Engineering",
    stages: [
      {
        round_name: "Phone Screen",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "breadth", label: "Full Stack Breadth", weight: 40 },
          { key: "experience", label: "Experience", weight: 30 },
          { key: "communication", label: "Communication", weight: 30 },
        ],
      },
      {
        round_name: "Live Coding",
        round_type: "coding_test",
        duration_minutes: 90,
        is_mandatory: true,
        criteria_schema: [
          { key: "frontend", label: "Frontend Skills", weight: 30 },
          { key: "backend", label: "Backend Skills", weight: 35 },
          { key: "integration", label: "Integration Thinking", weight: 35 },
        ],
      },
      {
        round_name: "Architecture Discussion",
        round_type: "technical_round",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "design", label: "System Design", weight: 40 },
          { key: "tradeoffs", label: "Trade-off Analysis", weight: 30 },
          { key: "devops", label: "DevOps Awareness", weight: 30 },
        ],
      },
      {
        round_name: "Culture Fit",
        round_type: "cultural_fit",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "collaboration", label: "Collaboration", weight: 35 },
          { key: "adaptability", label: "Adaptability", weight: 35 },
          { key: "values", label: "Values", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "DevOps Engineer",
    description: "3-round pipeline for DevOps Engineers",
    category: "IT / Engineering",
    stages: [
      {
        round_name: "Technical Screening",
        round_type: "technical_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "infra", label: "Infrastructure Knowledge", weight: 35 },
          { key: "ci_cd", label: "CI/CD Pipelines", weight: 35 },
          { key: "cloud", label: "Cloud Platforms", weight: 30 },
        ],
      },
      {
        round_name: "Infrastructure Scenario",
        round_type: "case_study",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "architecture", label: "Architecture", weight: 35 },
          { key: "security", label: "Security Practices", weight: 30 },
          { key: "troubleshooting", label: "Troubleshooting", weight: 35 },
        ],
      },
      {
        round_name: "Final Interview",
        round_type: "final_round",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "team_fit", label: "Team Fit", weight: 35 },
          { key: "incident_mgmt", label: "Incident Management", weight: 35 },
          {
            key: "automation_mindset",
            label: "Automation Mindset",
            weight: 30,
          },
        ],
      },
    ],
  },
  {
    template_name: "Data Engineer",
    description: "4-round pipeline for Data Engineers",
    category: "IT / Engineering",
    stages: [
      {
        round_name: "Phone Screen",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "data_fundamentals", label: "Data Fundamentals", weight: 40 },
          { key: "experience", label: "Experience", weight: 30 },
          { key: "communication", label: "Communication", weight: 30 },
        ],
      },
      {
        round_name: "SQL/ETL Assessment",
        round_type: "coding_test",
        duration_minutes: 90,
        is_mandatory: true,
        criteria_schema: [
          { key: "sql", label: "SQL Proficiency", weight: 40 },
          { key: "etl", label: "ETL Design", weight: 30 },
          { key: "optimization", label: "Query Optimization", weight: 30 },
        ],
      },
      {
        round_name: "Data Modeling",
        round_type: "technical_round",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "modeling", label: "Data Modeling", weight: 35 },
          { key: "pipeline", label: "Pipeline Architecture", weight: 35 },
          { key: "tools", label: "Tool Ecosystem", weight: 30 },
        ],
      },
      {
        round_name: "Final Panel",
        round_type: "final_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "problem_solving", label: "Problem Solving", weight: 35 },
          { key: "ownership", label: "Ownership", weight: 35 },
          { key: "culture", label: "Culture Fit", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "QA/Test Engineer",
    description: "3-round pipeline for QA Engineers",
    category: "IT / Engineering",
    stages: [
      {
        round_name: "Technical Screening",
        round_type: "technical_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          {
            key: "testing_fundamentals",
            label: "Testing Fundamentals",
            weight: 35,
          },
          { key: "automation", label: "Automation Skills", weight: 35 },
          { key: "tools", label: "Tools Knowledge", weight: 30 },
        ],
      },
      {
        round_name: "Test Case Design",
        round_type: "assignment",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "test_design", label: "Test Design", weight: 40 },
          { key: "edge_cases", label: "Edge Case Discovery", weight: 30 },
          { key: "documentation", label: "Documentation", weight: 30 },
        ],
      },
      {
        round_name: "Manager Interview",
        round_type: "managerial_round",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "collaboration", label: "Collaboration", weight: 35 },
          { key: "quality_mindset", label: "Quality Mindset", weight: 35 },
          { key: "culture", label: "Culture Fit", weight: 30 },
        ],
      },
    ],
  },
  {
    template_name: "Product Manager",
    description: "4-round pipeline for Product Managers",
    category: "IT / Engineering",
    stages: [
      {
        round_name: "Phone Screen",
        round_type: "screening",
        duration_minutes: 30,
        is_mandatory: true,
        criteria_schema: [
          { key: "product_sense", label: "Product Sense", weight: 40 },
          { key: "experience", label: "Experience", weight: 30 },
          { key: "communication", label: "Communication", weight: 30 },
        ],
      },
      {
        round_name: "Product Case Study",
        round_type: "case_study",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "problem_framing", label: "Problem Framing", weight: 35 },
          { key: "prioritization", label: "Prioritization", weight: 35 },
          { key: "metrics", label: "Metrics & Success", weight: 30 },
        ],
      },
      {
        round_name: "Cross-functional Simulation",
        round_type: "group_discussion",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          {
            key: "stakeholder_mgmt",
            label: "Stakeholder Management",
            weight: 35,
          },
          { key: "technical_depth", label: "Technical Depth", weight: 30 },
          { key: "decision_making", label: "Decision Making", weight: 35 },
        ],
      },
      {
        round_name: "Final Panel",
        round_type: "final_round",
        duration_minutes: 45,
        is_mandatory: true,
        criteria_schema: [
          { key: "vision", label: "Product Vision", weight: 35 },
          { key: "leadership", label: "Leadership", weight: 35 },
          { key: "culture", label: "Culture Fit", weight: 30 },
        ],
      },
    ],
  },
];

// ────────────────── Category colors ──────────────────
const CATEGORY_COLORS: Record<string, string> = {
  Sales: "#f59e0b",
  "Digital Marketing": "#ec4899",
  "IT / Engineering": "#3b82f6",
};

// ────────────────── Component ──────────────────

const RoundTemplateBuilder: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const templates = useSelector(selectRoundTemplates);
  const loading = useSelector(selectRoundTemplatesLoading);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [templateName, setTemplateName] = useState("");
  const [description, setDescription] = useState("");
  const [stages, setStages] = useState<SeedStage[]>([]);

  useEffect(() => {
    dispatch(fetchRoundTemplates());
  }, [dispatch]);

  const resetForm = () => {
    setTemplateName("");
    setDescription("");
    setStages([]);
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (tpl: any) => {
    setEditingId(tpl.id);
    setTemplateName(tpl.template_name || tpl.name || "");
    setDescription(tpl.description || "");
    setStages(
      (tpl.stages || []).map((s: any) => ({
        round_name: s.round_name || "",
        round_type: s.round_type || "screening",
        duration_minutes: s.duration_minutes || 60,
        is_mandatory: s.is_mandatory !== false,
        criteria_schema: s.criteria_schema || [],
      })),
    );
    setModalOpen(true);
  };

  const applySeedTemplate = (seed: SeedTemplate) => {
    resetForm();
    setTemplateName(seed.template_name);
    setDescription(seed.description);
    setStages([...seed.stages]);
    setModalOpen(true);
  };

  const addStage = () => {
    setStages((prev) => [
      ...prev,
      {
        round_name: "",
        round_type: "screening",
        duration_minutes: 60,
        is_mandatory: true,
        criteria_schema: [
          { key: "skill_1", label: "Criteria 1", weight: 50 },
          { key: "skill_2", label: "Criteria 2", weight: 50 },
        ],
      },
    ]);
  };

  const removeStage = (idx: number) => {
    setStages((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateStage = (idx: number, field: string, value: any) => {
    setStages((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)),
    );
  };

  const updateCriteria = (
    stageIdx: number,
    critIdx: number,
    field: string,
    value: any,
  ) => {
    setStages((prev) =>
      prev.map((s, si) =>
        si === stageIdx
          ? {
              ...s,
              criteria_schema: s.criteria_schema.map((c, ci) =>
                ci === critIdx ? { ...c, [field]: value } : c,
              ),
            }
          : s,
      ),
    );
  };

  const addCriteria = (stageIdx: number) => {
    setStages((prev) =>
      prev.map((s, si) =>
        si === stageIdx
          ? {
              ...s,
              criteria_schema: [
                ...s.criteria_schema,
                { key: `criteria_${Date.now()}`, label: "", weight: 0 },
              ],
            }
          : s,
      ),
    );
  };

  const removeCriteria = (stageIdx: number, critIdx: number) => {
    setStages((prev) =>
      prev.map((s, si) =>
        si === stageIdx
          ? {
              ...s,
              criteria_schema: s.criteria_schema.filter(
                (_, ci) => ci !== critIdx,
              ),
            }
          : s,
      ),
    );
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      notify.error("Template name is required");
      return;
    }
    if (stages.length === 0) {
      notify.error("At least one round stage is required");
      return;
    }
    for (const stage of stages) {
      if (!stage.round_name.trim()) {
        notify.error("All stages must have a round name");
        return;
      }
      const totalWeight = stage.criteria_schema.reduce(
        (s, c) => s + (c.weight || 0),
        0,
      );
      if (stage.criteria_schema.length > 0 && totalWeight !== 100) {
        notify.error(
          `Criteria weights for "${stage.round_name}" must sum to 100 (currently ${totalWeight})`,
        );
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        template_name: templateName.trim(),
        description: description.trim(),
        stages,
      };

      if (editingId) {
        await dispatch(
          updateRoundTemplate({ templateId: editingId, templateData: payload }),
        ).unwrap();
        notify.success("Template updated");
      } else {
        await dispatch(createRoundTemplate(payload)).unwrap();
        notify.success("Template created");
      }
      setModalOpen(false);
      resetForm();
      dispatch(fetchRoundTemplates());
    } catch (err: any) {
      notify.error(typeof err === "string" ? err : "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId: number) => {
    try {
      await dispatch(deleteRoundTemplate(templateId)).unwrap();
      notify.success("Template deleted");
      dispatch(fetchRoundTemplates());
    } catch (err: any) {
      notify.error(typeof err === "string" ? err : "Failed to delete template");
    }
  };

  // Group seed templates by category
  const seedByCategory = useMemo(() => {
    const map: Record<string, SeedTemplate[]> = {};
    SEED_TEMPLATES.forEach((t) => {
      if (!map[t.category]) map[t.category] = [];
      map[t.category].push(t);
    });
    return map;
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="card">
        <div className="card-header border-0 pb-0">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1 d-flex align-items-center">
                <i className="ti ti-template text-primary fs-32 me-2" />
                Round Templates
              </h4>
              <p className="text-muted mb-0">
                Create reusable interview round pipelines for different job
                roles
              </p>
            </div>
            <Button
              type="primary"
              onClick={openCreate}
              icon={<i className="ti ti-plus me-1" />}>
              Create Template
            </Button>
          </div>
        </div>
      </div>

      {/* Existing templates */}
      <div className="mt-3">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : templates.length > 0 ? (
          <div className="row g-3">
            {templates.map((tpl: any) => (
              <div key={tpl.id} className="col-md-6 col-lg-4">
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    padding: "18px 20px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "box-shadow 0.2s",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}>
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <div>
                      <h6
                        className="fw-semibold mb-1"
                        style={{ fontSize: 14, color: "#0f172a" }}>
                        {tpl.template_name || tpl.name}
                      </h6>
                      <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                        {tpl.description || "No description"}
                      </p>
                    </div>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: "edit",
                            label: "Edit",
                            icon: <i className="ti ti-pencil" />,
                            onClick: () => openEdit(tpl),
                          },
                          { type: "divider" },
                          {
                            key: "delete",
                            label: "Delete",
                            icon: <i className="ti ti-trash" />,
                            danger: true,
                            onClick: () => handleDelete(tpl.id),
                          },
                        ],
                      }}
                      trigger={["click"]}>
                      <button
                        className="btn btn-sm btn-light"
                        style={{ borderRadius: 6 }}>
                        <i className="ti ti-dots-vertical" />
                      </button>
                    </Dropdown>
                  </div>
                  <div className="d-flex flex-wrap gap-1 mt-auto pt-2">
                    <Tag color="blue">{(tpl.stages || []).length} Rounds</Tag>
                    {(tpl.stages || []).slice(0, 3).map((s: any, i: number) => (
                      <Tag key={i} style={{ fontSize: 11 }}>
                        {s.round_type?.replace(/_/g, " ")}
                      </Tag>
                    ))}
                    {(tpl.stages || []).length > 3 && (
                      <Tag style={{ fontSize: 11 }}>
                        +{(tpl.stages || []).length - 3}
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "2px dashed #e2e8f0",
              padding: "40px 20px",
              textAlign: "center",
            }}>
            <i
              className="ti ti-template"
              style={{ fontSize: "3rem", color: "#cbd5e1", marginBottom: 12 }}
            />
            <h6 className="fw-semibold" style={{ color: "#64748b" }}>
              No templates yet
            </h6>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              Create templates or use a pre-built one from below
            </p>
            <Button type="primary" onClick={openCreate}>
              Create Template
            </Button>
          </div>
        )}
      </div>

      {/* Pre-built seed templates */}
      <div className="mt-4">
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            padding: "20px",
          }}>
          <h6
            className="fw-semibold mb-1"
            style={{ fontSize: 14, color: "#0f172a" }}>
            <i className="ti ti-sparkles me-2 text-warning" />
            Pre-built Templates
          </h6>
          <p className="text-muted mb-3" style={{ fontSize: 12.5 }}>
            Click any template to use it as a starting point. You can customize
            it before saving.
          </p>

          <Collapse
            ghost
            size="small"
            defaultActiveKey={[
              "Sales",
              "Digital Marketing",
              "IT / Engineering",
            ]}>
            {Object.entries(seedByCategory).map(([category, seeds]) => (
              <Panel
                key={category}
                header={
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: CATEGORY_COLORS[category] || "#64748b",
                        marginRight: 8,
                      }}
                    />
                    {category}
                    <span
                      className="text-muted ms-2"
                      style={{ fontWeight: 400, fontSize: 12 }}>
                      ({seeds.length} templates)
                    </span>
                  </span>
                }>
                <div className="row g-2">
                  {seeds.map((seed, idx) => (
                    <div key={idx} className="col-md-6 col-lg-4">
                      <div
                        onClick={() => applySeedTemplate(seed)}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: "12px 14px",
                          cursor: "pointer",
                          transition: "all 0.15s",
                          background: "#fafafa",
                        }}
                        onMouseEnter={(e) => {
                          (
                            e.currentTarget as HTMLDivElement
                          ).style.borderColor =
                            CATEGORY_COLORS[category] || "#3b82f6";
                          (e.currentTarget as HTMLDivElement).style.background =
                            "#fff";
                        }}
                        onMouseLeave={(e) => {
                          (
                            e.currentTarget as HTMLDivElement
                          ).style.borderColor = "#e2e8f0";
                          (e.currentTarget as HTMLDivElement).style.background =
                            "#fafafa";
                        }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <i
                            className="ti ti-layout-list"
                            style={{
                              color: CATEGORY_COLORS[category] || "#64748b",
                              fontSize: 14,
                            }}
                          />
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 13,
                              color: "#0f172a",
                            }}>
                            {seed.template_name}
                          </span>
                        </div>
                        <p
                          className="text-muted mb-1"
                          style={{ fontSize: 11.5, lineHeight: 1.3 }}>
                          {seed.description}
                        </p>
                        <div className="d-flex gap-1 flex-wrap">
                          <Tag color="blue" style={{ fontSize: 10 }}>
                            {seed.stages.length} rounds
                          </Tag>
                          {seed.stages.slice(0, 2).map((s, i) => (
                            <Tag key={i} style={{ fontSize: 10 }}>
                              {s.round_type.replace(/_/g, " ")}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            ))}
          </Collapse>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        title={
          <span>
            <i className={`ti ${editingId ? "ti-pencil" : "ti-plus"} me-2`} />
            {editingId ? "Edit Template" : "Create Round Template"}
          </span>
        }
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          resetForm();
        }}
        onOk={handleSave}
        okText={editingId ? "Update" : "Create"}
        confirmLoading={saving}
        width={720}
        destroyOnClose>
        <div className="d-flex flex-column gap-3 mt-3">
          <div>
            <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
              Template Name *
            </label>
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g. Frontend Developer Pipeline"
            />
          </div>
          <div>
            <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
              Description
            </label>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this interview pipeline"
              rows={2}
            />
          </div>

          {/* Stages */}
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label
                className="form-label fw-semibold mb-0"
                style={{ fontSize: 12 }}>
                Interview Stages ({stages.length})
              </label>
              <Button
                size="small"
                type="dashed"
                onClick={addStage}
                icon={<i className="ti ti-plus" style={{ fontSize: 12 }} />}>
                Add Stage
              </Button>
            </div>

            {stages.length === 0 && (
              <div
                style={{
                  padding: "20px",
                  border: "2px dashed #e2e8f0",
                  borderRadius: 10,
                  textAlign: "center",
                  color: "#94a3b8",
                }}>
                <i
                  className="ti ti-layers-subtract"
                  style={{ fontSize: 24, display: "block", marginBottom: 8 }}
                />
                No stages added. Click "Add Stage" to begin.
              </div>
            )}

            <div className="d-flex flex-column gap-3">
              {stages.map((stage, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "14px 16px",
                    background: "#fafbfc",
                  }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        background: "#eff6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#3b82f6",
                        flexShrink: 0,
                      }}>
                      {idx + 1}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0f172a",
                        flex: 1,
                      }}>
                      Round {idx + 1}
                    </span>
                    <button
                      className="btn btn-sm"
                      style={{
                        padding: "2px 8px",
                        color: "#ef4444",
                        border: "1px solid #fecaca",
                        borderRadius: 6,
                      }}
                      onClick={() => removeStage(idx)}>
                      <i className="ti ti-trash" style={{ fontSize: 13 }} />
                    </button>
                  </div>

                  <div className="row g-2 mb-2">
                    <div className="col-md-5">
                      <Input
                        size="small"
                        value={stage.round_name}
                        onChange={(e) =>
                          updateStage(idx, "round_name", e.target.value)
                        }
                        placeholder="Round name"
                      />
                    </div>
                    <div className="col-md-4">
                      <Select
                        size="small"
                        style={{ width: "100%" }}
                        value={stage.round_type}
                        onChange={(v) => updateStage(idx, "round_type", v)}
                        options={ROUND_TYPE_OPTIONS}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputNumber
                        size="small"
                        style={{ width: "100%" }}
                        value={stage.duration_minutes}
                        onChange={(v) =>
                          updateStage(idx, "duration_minutes", v || 60)
                        }
                        min={15}
                        max={480}
                        addonAfter="min"
                      />
                    </div>
                  </div>

                  {/* Criteria */}
                  <div style={{ marginTop: 8 }}>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}>
                        Evaluation Criteria
                        {stage.criteria_schema.length > 0 && (
                          <span className="ms-1" style={{ fontWeight: 400 }}>
                            (total:{" "}
                            {stage.criteria_schema.reduce(
                              (s, c) => s + (c.weight || 0),
                              0,
                            )}
                            %)
                          </span>
                        )}
                      </span>
                      <button
                        className="btn btn-sm"
                        style={{
                          fontSize: 11,
                          padding: "1px 8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: 6,
                          color: "#64748b",
                        }}
                        onClick={() => addCriteria(idx)}>
                        + Criteria
                      </button>
                    </div>
                    {stage.criteria_schema.map((c, ci) => (
                      <div
                        key={ci}
                        className="d-flex align-items-center gap-2 mb-1">
                        <Input
                          size="small"
                          style={{ flex: 1 }}
                          value={c.label}
                          onChange={(e) =>
                            updateCriteria(idx, ci, "label", e.target.value)
                          }
                          placeholder="Criteria label"
                        />
                        <InputNumber
                          size="small"
                          style={{ width: 80 }}
                          value={c.weight}
                          onChange={(v) =>
                            updateCriteria(idx, ci, "weight", v || 0)
                          }
                          min={0}
                          max={100}
                          addonAfter="%"
                        />
                        <button
                          className="btn btn-sm"
                          style={{
                            padding: "1px 6px",
                            color: "#94a3b8",
                            border: "1px solid #e2e8f0",
                            borderRadius: 4,
                          }}
                          onClick={() => removeCriteria(idx, ci)}
                          disabled={stage.criteria_schema.length <= 1}>
                          <i className="ti ti-x" style={{ fontSize: 11 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RoundTemplateBuilder;
